import { useState } from "react";
import axios from "axios";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";
import { toast } from "sonner";
import { ShieldAlert, Trash2, Lock, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

/**
 * Tela de limpeza administrativa.
 * Cada target dispara POST /api/admin/cleanup/{target} com senha do admin.
 * HARD DELETE - operacao irreversivel. Protegida por senha + role admin.
 */
const TARGETS = [
  { key: "contratos_cancelados", label: "Contratos Cancelados", desc: "Apenas contratos com status = cancelado", color: "bg-red-50 text-red-700 border-red-200" },
  { key: "contratos", label: "Todos os Contratos", desc: "Apaga todos os contratos (incluindo ativos!)", color: "bg-red-100 text-red-800 border-red-300", danger: true },
  { key: "orcamentos", label: "Todas as Propostas/Orçamentos", desc: "Apaga todos os orçamentos", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "contas_receber", label: "Contas a Receber", desc: "Apenas lançamentos tipo=receita", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "contas_pagar", label: "Contas a Pagar", desc: "Apenas lançamentos tipo=despesa", color: "bg-rose-50 text-rose-700 border-rose-200" },
  { key: "lancamentos", label: "Todos os Lançamentos", desc: "Apaga todos os lançamentos (receitas + despesas)", color: "bg-red-100 text-red-800 border-red-300", danger: true },
  { key: "cartoes", label: "Cartões de Crédito", desc: "Apaga apenas os cadastros de cartões", color: "bg-slate-50 text-slate-700 border-slate-200" },
  { key: "cartoes_despesas", label: "Despesas de Cartão", desc: "Apaga lançamentos de despesa do cartão", color: "bg-slate-50 text-slate-700 border-slate-200" },
  { key: "cartoes_parcelas", label: "Parcelas de Cartão", desc: "Apaga todas as parcelas de cartão", color: "bg-slate-50 text-slate-700 border-slate-200" },
  { key: "cartoes_faturas", label: "Faturas Fechadas", desc: "Apaga histórico de faturas fechadas", color: "bg-slate-50 text-slate-700 border-slate-200" },
  { key: "crm_leads", label: "Leads do CRM", desc: "Apaga todos os leads do funil comercial", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "crm_notifications", label: "Notificações CRM", desc: "Apaga histórico de notificações da sineta", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "fornecedores", label: "Fornecedores", desc: "Apaga apenas registros com tipo_cadastro=fornecedor (clientes preservados)", color: "bg-purple-50 text-purple-700 border-purple-200" },
];

export default function AdminCleanupPage() {
  const [selected, setSelected] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState("");
  const [executing, setExecuting] = useState(false);
  const [results, setResults] = useState(null);

  const toggleTarget = (k) => {
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const handleExecute = async () => {
    if (!password || selected.size === 0) return;
    setExecuting(true);
    setResults(null);
    const out = [];
    for (const target of selected) {
      try {
        const r = await axios.post(`${API}/admin/cleanup/${target}`,
          { password },
          { skipAuthInterceptor: true });
        out.push({ target, ok: true, ...r.data });
      } catch (err) {
        out.push({ target, ok: false, error: err.response?.data?.detail || err.message });
        if (err.response?.status === 401) {
          toast.error("Senha incorreta - operação interrompida");
          setResults(out);
          setExecuting(false);
          return;
        }
      }
    }
    setResults(out);
    setExecuting(false);
    const totalRemoved = out.filter(o => o.ok).reduce((s, o) => s + (o.removidos || 0), 0);
    if (totalRemoved > 0) toast.success(`${totalRemoved} registros removidos`);
    setPassword("");
    setSelected(new Set());
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-5xl" data-testid="cleanup-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#0A1628] flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-red-600" />
            Limpeza Administrativa
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            <strong className="text-red-600">HARD DELETE</strong>: os dados removidos não podem ser recuperados.
            Cada operação exige sua senha e é registrada no log de auditoria.
          </p>
        </div>

        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 flex gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">Antes de executar:</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>O backup local automatizado roda diariamente — mas dados apagados aqui não voltam por backup automaticamente, é preciso restaurar manualmente.</li>
                <li>Esta operação é independente do código — redeploy NÃO faz dados voltarem.</li>
                <li>Senhas incorretas interrompem todo o lote (nada é apagado).</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Selecione as bases a limpar ({selected.size})</span>
              {selected.size > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelected(new Set())}
                  data-testid="btn-clear-selection"
                >
                  Limpar seleção
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {TARGETS.map(t => (
              <label
                key={t.key}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selected.has(t.key) ? t.color : "bg-white border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`target-${t.key}`}
              >
                <Checkbox
                  checked={selected.has(t.key)}
                  onCheckedChange={() => toggleTarget(t.key)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm">{t.label}</p>
                    {t.danger && <Badge className="bg-red-600 text-white text-[10px]">PERIGO</Badge>}
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">{t.desc}</p>
                </div>
              </label>
            ))}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            className="bg-red-600 hover:bg-red-700"
            disabled={selected.size === 0}
            onClick={() => { setPassword(""); setShowConfirm(true); }}
            data-testid="btn-execute"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Executar limpeza ({selected.size})
          </Button>
        </div>

        {results && (
          <Card className="border-0 shadow-sm" data-testid="results">
            <CardHeader>
              <CardTitle className="text-base">Resultado da execução</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {results.map((r, i) => (
                <div key={i} className={`flex items-center justify-between p-2 rounded ${r.ok ? "bg-emerald-50" : "bg-red-50"}`}>
                  <div className="flex items-center gap-2 text-sm">
                    {r.ok
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : <AlertCircle className="h-4 w-4 text-red-600" />}
                    <span className="font-medium">{r.target}</span>
                  </div>
                  <span className="text-sm font-mono">
                    {r.ok
                      ? `removidos: ${r.removidos} de ${r.antes}`
                      : `erro: ${r.error}`}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation dialog */}
      <Dialog open={showConfirm} onOpenChange={(o) => !o && setShowConfirm(false)}>
        <DialogContent className="max-w-md" data-testid="dialog-confirm">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" /> Confirmar exclusão definitiva
            </DialogTitle>
            <DialogDescription className="pt-2">
              Você está prestes a apagar <strong>{selected.size}</strong> {selected.size === 1 ? "base" : "bases"} de dados.
              Esta ação é <strong>irreversível</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-slate-50 rounded p-3 space-y-1 max-h-40 overflow-y-auto">
              {Array.from(selected).map(k => {
                const t = TARGETS.find(x => x.key === k);
                return <p key={k} className="text-xs font-mono">• {t?.label}</p>;
              })}
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Lock className="h-3 w-3" /> Digite sua senha de admin</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && password) handleExecute(); }}
                placeholder="Senha do usuário logado"
                data-testid="input-confirm-password"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={executing}>
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => { await handleExecute(); setShowConfirm(false); }}
              disabled={!password || executing}
              data-testid="btn-confirm-execute"
            >
              {executing
                ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Executando...</>)
                : (<><Trash2 className="h-4 w-4 mr-1" /> Executar agora</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
